import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Key, Shield, Loader2, Save, Plus, Trash2, FileBarChart } from "lucide-react";
import { toast } from "sonner";

export default function RolesAdmin() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState(null);
  const [menuIds, setMenuIds] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState("");

  const { data: roles } = useQuery({ queryKey: ["admin-roles"], queryFn: async () => (await api.get("/admin/roles")).data });
  const { data: menu } = useQuery({ queryKey: ["admin-menu"], queryFn: async () => (await api.get("/admin/menu")).data });
  const { data: perms, isFetching } = useQuery({
    queryKey: ["role-perms", selected],
    queryFn: async () => (await api.get(`/admin/roles/${selected}/permissions`)).data,
    enabled: !!selected,
  });

  useEffect(() => { if (roles?.length && !selected) setSelected(roles.find((r) => !r.is_global)?.key || roles[0].key); }, [roles, selected]);
  useEffect(() => {
    if (perms) setMenuIds(perms.menu_ids || []);
  }, [perms]);

  const reports = (menu || []).filter((m) =>
    m.type === "reporte" ||
    m.type === "capability" ||
    (m.type === "menu" && m.route && m.id !== "estructura_organizacional" && !m.admin_only)
  );
  const menuById = Object.fromEntries((menu || []).map((m) => [m.id, m]));
  const reportLabel = (r) => {
    const p = menuById[r.parent_id];
    return p ? `${p.name} · ${r.name}` : r.name;
  };

  const createRole = useMutation({
    mutationFn: async (name) => (await api.post("/admin/roles", { name })).data,
    onSuccess: (r) => { toast.success("Rol creado"); setNewOpen(false); setNewName(""); qc.invalidateQueries({ queryKey: ["admin-roles"] }); setSelected(r.key); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const deleteRole = useMutation({
    mutationFn: async (key) => (await api.delete(`/admin/roles/${key}`)).data,
    onSuccess: (_d, key) => { toast.success("Rol eliminado"); if (selected === key) setSelected(null); qc.invalidateQueries({ queryKey: ["admin-roles"] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const saveMenus = useMutation({
    mutationFn: async () => (await api.put(`/admin/roles/${selected}/menus`, { menu_ids: menuIds })).data,
    onSuccess: () => { toast.success("Permisos de reportes guardados"); qc.invalidateQueries({ queryKey: ["role-perms", selected] }); qc.invalidateQueries({ queryKey: ["admin-roles"] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const toggleMenu = (id) => setMenuIds((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const toggleAll = () => setMenuIds((p) => reports.every((r) => p.includes(r.id)) ? p.filter((x) => !reports.some((r) => r.id === x)) : [...new Set([...p, ...reports.map((r) => r.id)])]);
  const isGlobal = perms?.is_global;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-red-500 font-semibold">Administración</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white tracking-tight flex items-center gap-2">
            <Key className="w-8 h-8 text-red-500" /> Roles y Permisos
          </h1>
          <p className="text-slate-400 text-sm mt-1">Crea roles como plantilla de reportes y módulos permitidos. La estructura organizacional se asigna por usuario.</p>
        </div>
        <Button onClick={() => setNewOpen(true)} className="bg-red-600 hover:bg-red-700 text-white" data-testid="create-role-button">
          <Plus className="w-4 h-4 mr-2" /> Nuevo Rol
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Roles list */}
        <div className="lg:col-span-3 bg-[#131C2E] border border-[#223048] rounded-xl p-3 shadow-lg h-fit">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-2 mb-2">Roles</p>
          <div className="space-y-1">
            {roles?.map((r) => (
              <div
                key={r.key}
                className={`group w-full flex items-center rounded-lg transition-colors ${selected === r.key ? "bg-red-600/20" : "hover:bg-white/5"}`}
              >
                <button
                  onClick={() => setSelected(r.key)}
                  data-testid={`role-item-${r.key}`}
                  className={`flex-1 text-left px-3 py-2.5 ${selected === r.key ? "text-red-300" : "text-slate-300"}`}
                >
                  <p className="text-sm font-semibold flex items-center gap-1.5">
                    {r.is_global && <Shield className="w-3.5 h-3.5" />} {r.name}
                  </p>
                  <p className="text-[11px] text-slate-500">{r.user_count} usuarios · {r.is_global ? "acceso total" : `${r.report_count} menús`}</p>
                </button>
                {!r.is_global && (
                  <button
                    onClick={() => { if (window.confirm(`¿Eliminar el rol "${r.name}"?`)) deleteRole.mutate(r.key); }}
                    data-testid={`delete-role-${r.key}`}
                    title="Eliminar rol"
                    className="opacity-0 group-hover:opacity-100 transition-opacity px-2.5 text-slate-400 hover:text-red-400 shrink-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Permissions editor */}
        <div className="lg:col-span-9 space-y-6">
          {isFetching && !perms ? (
            <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 text-red-600 animate-spin" /></div>
          ) : isGlobal ? (
            <div className="bg-[#131C2E] border border-[#223048] rounded-xl p-8 text-center text-slate-300">
              <Shield className="w-10 h-10 text-red-500 mx-auto mb-3" />
              <p className="font-semibold text-white">Administrador Global</p>
              <p className="text-sm text-slate-400 mt-1">Este rol tiene acceso a todos los reportes.</p>
            </div>
          ) : (
            <>
              {/* Reports */}
              <div className="bg-[#131C2E] border border-[#223048] rounded-xl p-6 shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-display text-lg font-bold text-slate-100 flex items-center gap-2"><FileBarChart className="w-4 h-4 text-red-500" /> Reportes y módulos permitidos</h2>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={toggleAll} className="border-[#223048] text-slate-300" data-testid="toggle-all-reports">Todos / Ninguno</Button>
                    <Button size="sm" onClick={() => saveMenus.mutate()} disabled={saveMenus.isPending} className="bg-red-600 hover:bg-red-700 text-white" data-testid="save-menus-button">
                      {saveMenus.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" />Guardar</>}
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {reports.map((r) => (
                    <label key={r.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-slate-900/40 border border-[#223048] cursor-pointer hover:border-red-600/40" data-testid={`report-perm-${r.id}`}>
                      <Checkbox checked={menuIds.includes(r.id)} onCheckedChange={() => toggleMenu(r.id)} className="border-slate-600 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600" />
                      <span className="text-sm text-slate-200">{reportLabel(r)}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Structure scope */}
            </>
          )}
        </div>
      </div>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="bg-[#131C2E] border-[#223048] text-slate-200">
          <DialogHeader>
            <DialogTitle className="text-white font-display">Nuevo Rol</DialogTitle>
            <DialogDescription className="text-slate-400">Crea un rol como plantilla de reportes y módulos permitidos.</DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); if (newName.trim()) createRole.mutate(newName.trim()); }} className="space-y-4">
            <div>
              <Label className="text-slate-300">Nombre del rol</Label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} required autoFocus
                placeholder="Ej. Supervisor Regional" data-testid="new-role-name-input"
                className="mt-1 bg-slate-950/60 border-slate-700 text-white" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={createRole.isPending} className="bg-red-600 hover:bg-red-700 text-white" data-testid="submit-role-button">
                {createRole.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crear rol"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
